// frontend/src/pages/dashboards/DashboardGrid.jsx
// Phase 4 step 14a-2: the dashboard grid. It places tiles and, in edit mode,
// lets people move and resize them with a pointer (mouse, pen or touch) or the
// keyboard. What goes inside a tile is the page's business (renderTile); the
// rules are in gridLayout.js and the pointer logic in gridDrag.js, both pure.
//
// At 900 px and wider the grid is 12 columns; narrower, tiles stack at full
// width in reading order and cannot be moved (the page says why).
//
// Pointer: press anywhere on a tile that is not one of its own controls and
// drag to move it; drag the corner handle to resize. The tile follows the
// pointer and a dashed outline shows where it will land. Escape cancels.
// Keyboard (a tile focused): arrows move it, Shift + arrows resize it.
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  COLUMNS, GAP, ROW_HEIGHT, STACK_BELOW,
  columnWidth, describePlace, gridHeight, nudge, readingOrder, stackedHeight, tileRect,
} from './gridLayout';
import { IDLE, beginDrag, cancelDrag, dragTo, draggedKey, endDrag, ghostRect } from './gridDrag';

const EDIT_ROOM_ROWS = 4;               // empty rows under the last tile in edit mode, to drag into
const INTERACTIVE = 'button, a, input, textarea, select, label, [role="button"], [role="menuitem"], [contenteditable="true"], [data-no-drag]';
const KEYS = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
// Layout effects only mean something in a browser; this keeps static rendering (tests) quiet.
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** The grid's width, measured; `initial` is used before the first measurement (and when rendering on a server). */
function useWidth(initial) {
  const [width, setWidth] = useState(initial);
  const [el, setEl] = useState(null);
  useIsoLayoutEffect(() => {
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const read = () => setWidth(Math.floor(el.getBoundingClientRect().width));
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [width, setEl];
}

/**
 * tiles       [{key, kind, x, y, w, h, ...}] in any order
 * editing     true shows the column guides and handles and enables moving
 * onChange    (tiles) => void, called with the new layout after a drop or a keyboard move
 * renderTile  (tile, {editing, dragging, stacked}) => node, the tile's content
 * tileLabel   (tile) => string, the tile's name for screen readers
 */
export default function DashboardGrid({
  tiles, editing = false, onChange, renderTile, tileLabel = () => 'Tile', initialWidth = 1200, className,
}) {
  const [width, setRef] = useWidth(initialWidth);
  const [drag, setDragState] = useState(IDLE);
  const dragRef = useRef(IDLE);
  const [announcement, setAnnouncement] = useState('');
  const focusKey = useRef(null);
  const tileEls = useRef(new Map());
  const helpId = useId();

  const stacked = width < STACK_BELOW;
  const colWidth = columnWidth(width);
  const canEdit = editing && !stacked;

  const setDrag = useCallback((next) => {
    dragRef.current = next;
    setDragState(next);
  }, []);

  // Leaving edit mode or going narrow mid-drag drops the drag.
  useEffect(() => {
    if (!canEdit && dragRef.current.mode !== 'idle') setDrag(cancelDrag());
  }, [canEdit, setDrag]);

  // Escape cancels a drag wherever focus is.
  useEffect(() => {
    if (drag.mode === 'idle') return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDrag(cancelDrag());
        setAnnouncement('Move cancelled.');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drag.mode, setDrag]);

  // Keep focus on a tile after a keyboard move re-orders the DOM.
  useIsoLayoutEffect(() => {
    const key = focusKey.current;
    if (key == null) return;
    focusKey.current = null;
    const el = tileEls.current.get(key);
    if (el && document.activeElement !== el) el.focus({ preventScroll: true });
  });

  const commit = (next, key, verb) => {
    if (!next || next === tiles) return;
    onChange?.(next);
    const t = next.find((x) => x.key === key);
    if (t) setAnnouncement(`${tileLabel(t)} ${verb}: ${describePlace(t)}.`);
  };

  const onPointerDown = (e, tile) => {
    if (!canEdit || e.button !== 0 || dragRef.current.mode !== 'idle') return;
    const handle = e.target.closest?.('[data-resize-handle]');
    if (!handle && e.target.closest?.(INTERACTIVE)) return;    // the tile's own controls work as usual
    e.preventDefault();
    e.currentTarget.focus({ preventScroll: true });
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag(beginDrag(tiles, tile.key, handle ? 'resize' : 'move', { x: e.clientX, y: e.clientY }));
  };

  const onPointerMove = (e) => {
    const s = dragRef.current;
    if (s.mode === 'idle') return;
    const next = dragTo(s, { x: e.clientX, y: e.clientY }, colWidth);
    if (next !== s) setDrag(next);
  };

  const onPointerUp = () => {
    const s = dragRef.current;
    if (s.mode === 'idle') return;
    const { tiles: next, state } = endDrag(s);
    setDrag(state);
    commit(next, s.key, s.action === 'resize' ? 'resized' : 'moved');
  };

  const onPointerCancel = () => {
    if (dragRef.current.mode !== 'idle') setDrag(cancelDrag());
  };

  const onKeyDown = (e, tile) => {
    if (!canEdit || e.target !== e.currentTarget || dragRef.current.mode !== 'idle') return;
    const direction = KEYS[e.key];
    if (!direction || e.altKey || e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    const next = nudge(tiles, tile.key, direction, { resize: e.shiftKey });
    if (next === tiles) {
      setAnnouncement(`${tileLabel(tile)} cannot go further ${direction}.`);
      return;
    }
    focusKey.current = tile.key;
    commit(next, tile.key, e.shiftKey ? 'resized' : 'moved');
  };

  const register = (key) => (el) => {
    if (el) tileEls.current.set(key, el);
    else tileEls.current.delete(key);
  };

  if (stacked) {
    return (
      <div ref={setRef} className={cn('flex flex-col gap-3', className)} data-layout="stacked">
        {readingOrder(tiles).map((tile) => (
          <div key={tile.key} role="group" aria-label={tileLabel(tile)} className="relative min-w-0"
            style={{ height: stackedHeight(tile) }}>
            {renderTile(tile, { editing: false, dragging: false, stacked: true })}
          </div>
        ))}
      </div>
    );
  }

  const shown = drag.mode === 'dragging' ? drag.preview : tiles;
  const dragging = draggedKey(drag);
  const ghost = ghostRect(drag, colWidth, width);
  const height = gridHeight(shown) + (editing ? EDIT_ROOM_ROWS * (ROW_HEIGHT + GAP) : 0);
  const placeholder = dragging != null ? shown.find((t) => t.key === dragging) : null;
  const shownByKey = new Map(shown.map((t) => [t.key, t]));

  return (
    <div ref={setRef} className={cn('relative w-full', canEdit && 'select-none', className)} style={{ height }}
      data-layout="grid" data-editing={canEdit ? 'true' : 'false'}>
      {canEdit && (
        <>
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 grid"
            style={{ gridTemplateColumns: `repeat(${COLUMNS}, minmax(0, 1fr))`, columnGap: GAP }}>
            {Array.from({ length: COLUMNS }, (_, i) => <div key={i} className="rounded-xl bg-white/[0.018]" />)}
          </div>
          <p id={helpId} className="sr-only">
            Drag a tile to move it, or its corner to resize it. With a tile focused, arrow keys move it and Shift with
            arrow keys resizes it. Escape cancels a drag.
          </p>
        </>
      )}

      {placeholder && (
        <div aria-hidden="true" data-placeholder=""
          className="pointer-events-none absolute left-0 top-0 rounded-[22px] border border-dashed border-white/25 bg-white/[0.03]"
          style={rectStyle(tileRect(placeholder, colWidth))} />
      )}

      {/* DOM order follows the committed layout, never the preview: moving a node mid-drag would drop its pointer capture. */}
      {readingOrder(tiles).map((committed) => {
        const tile = shownByKey.get(committed.key) || committed;
        const isDragged = tile.key === dragging;
        const rect = isDragged && ghost ? ghost : tileRect(tile, colWidth);
        return (
          <div key={tile.key} ref={register(tile.key)} role="group" data-tile-key={tile.key}
            aria-roledescription="dashboard tile"
            aria-label={canEdit ? `${tileLabel(tile)}, ${describePlace(tile)}` : tileLabel(tile)}
            aria-describedby={canEdit ? helpId : undefined}
            tabIndex={canEdit ? 0 : undefined}
            onPointerDown={canEdit ? (e) => onPointerDown(e, tile) : undefined}
            onPointerMove={canEdit ? onPointerMove : undefined}
            onPointerUp={canEdit ? onPointerUp : undefined}
            onPointerCancel={canEdit ? onPointerCancel : undefined}
            onLostPointerCapture={canEdit ? onPointerCancel : undefined}
            onKeyDown={canEdit ? (e) => onKeyDown(e, tile) : undefined}
            className={cn(
              'absolute left-0 top-0 min-w-0 rounded-[22px]',
              canEdit && 'cursor-grab touch-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40',
              isDragged
                ? 'z-30 cursor-grabbing opacity-95 shadow-[0_18px_48px_rgba(0,0,0,0.55)]'
                : 'transition-transform duration-150 ease-out motion-reduce:transition-none',
            )}
            style={rectStyle(rect)}>
            {renderTile(tile, { editing: canEdit, dragging: isDragged, stacked: false })}
            {canEdit && (
              <span data-resize-handle="" aria-hidden="true" title="Drag to resize"
                className="absolute bottom-1.5 right-1.5 z-10 flex h-5 w-5 cursor-se-resize items-end justify-end rounded-md p-1 text-[var(--ui-text-muted)] hover:bg-white/10 hover:text-[var(--ui-text)]">
                <svg viewBox="0 0 8 8" className="h-2.5 w-2.5" fill="currentColor" aria-hidden="true">
                  <circle cx="7" cy="7" r="1" /><circle cx="3.5" cy="7" r="1" /><circle cx="7" cy="3.5" r="1" />
                </svg>
              </span>
            )}
          </div>
        );
      })}

      {canEdit && <div aria-live="polite" className="sr-only">{announcement}</div>}
    </div>
  );
}

function rectStyle(r) {
  return { transform: `translate(${r.left}px, ${r.top}px)`, width: r.width, height: r.height };
}
